import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useHistory } from 'react-router-dom';
import Collapse from '@mui/material/Collapse';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { FormInputSwitch } from '../../inputs/FormInputSwitch';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { PaymentCardsSelector } from './BankCardSelector';
import { AddingCard } from './AddingCard';
import {
  ButtonClassNames,
  InputRegexPattern,
  GlobalRouteNames,
  AccountSettingRouteNames,
  ModalVariants,
  LocalStorageItems,
} from '../../../models/Common';
import { TableBodyCell } from '../../../models/CustomTable';
import { View, AccountView, ExAmoView } from '../../../models/Calculate';
import { paymentsApi } from '../../../services/apiServices/user/payments';
import { regularPaymentsApi } from '../../../services/apiServices/user/regularPayments';
import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import { paymentsCardsApi } from '../../../services/apiServices/user/paymentsCards';
import { useDateLocalization } from '../../../hooks/useDateLocalization';
import { useCustomSnackbar } from '../../../hooks/useCustomEnqueueSnackbar';
import { modalSlice } from '../../../store/reducers/ModalSlice';
import { FormInputText } from '../../inputs/FormInputText';
import { ReactComponent as DeleteIcon } from '../../../assets/images/Delete.svg';
import { ReactComponent as SaveEmailIcon } from '../../../assets/images/ArrowSaveEmail.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/InfoOutlined.svg';
import { ReactComponent as LocationIcon } from '../../../assets/images/multiAccount/markerIcon.svg';
import { CustomSkeletonElement } from '../../commons/CustomSkeletonElement';
import { CustomNumberFormat } from '../../commons/CustomNumberFormat';
import { BasicTable } from '../../commons/customTables/BasicTable';
import { useDebounce } from '../../../hooks/useDebounce';
import { getErrorMessage } from '../../../utils';

interface AutoReplenishmentForm {
  isAutoReplenishment: boolean;
  paymentCard: string;
  autoReplenishmentEmail: string;
}

export const AutoReplenishment: FC = () => {
  const { t } = useTranslation();
  const { search } = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(search);
  const [processing, setProcessing] = useState<boolean>(queryParams.has('orderCode'));
  const attempts = useRef(0);
  const isAutoReplenishmentSet = useRef(false);
  const { formatDate } = useDateLocalization();
  const dispatch = useAppDispatch();
  const { customEnqueueSnackbar } = useCustomSnackbar();
  const { openModal } = modalSlice.actions;
  const { userInfoState } = useAppSelector((state) => state.userInfoReducer);
  const [openServices, setOpenServices] = useState(
    !localStorage.getItem(`${LocalStorageItems.ActiveServicesHidden}-${userInfoState.id}`),
  );
  const methods = useForm<AutoReplenishmentForm>({ mode: 'onChange' });
  const {
    setValue,
    watch,
    clearErrors,
    handleSubmit,
    setError,
    formState: { errors },
  } = methods;
  const { data: orderInfo, fulfilledTimeStamp } = paymentsApi.useGetOrdersInfoQuery(
    String(queryParams.get('orderCode')),
    {
      skip: !queryParams.has('orderCode'),
      pollingInterval: processing ? 5000 : 0,
    },
  );
  const { data: listOfCardsAPI, isFetching: isFetchingCardList } = paymentsCardsApi.useGetListOfCardsQuery('', {
    skip: processing,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: regularPaymentSettingsAPI,
    isLoading,
    isFetching,
    refetch,
  } = regularPaymentsApi.useGetSettingsQuery('', { skip: processing, refetchOnMountOrArgChange: true });
  const [deleteRegularPaymentEmail, { isLoading: isLoadingDeleteEmail }] =
    regularPaymentsApi.useDeleteRegularPaymentEmailMutation();
  const [switchRegularPayment] = regularPaymentsApi.useSwitchRegularPaymentMutation();
  const [changeRegularPaymentEmail, { isLoading: isLoadingChangeEmail }] =
    regularPaymentsApi.useChangeRegularPaymentEmailMutation();
  const [setPaymentCard] = regularPaymentsApi.useSetPaymentCardMutation();

  const isRegularPayment = regularPaymentSettingsAPI?.Active;
  const emailExp = new RegExp(InputRegexPattern.Email);
  const isValidEmail = emailExp.test(watch().autoReplenishmentEmail || '');
  const isSaveEmailDisabled =
    isLoadingChangeEmail ||
    isFetching ||
    !(isValidEmail && watch().autoReplenishmentEmail !== regularPaymentSettingsAPI?.Email);
  const isDeleteEmailDisabled = isLoadingDeleteEmail || isFetching || !regularPaymentSettingsAPI?.Email;
  const isAvailableRegularPayment =
    regularPaymentSettingsAPI?.Card &&
    Object.entries(regularPaymentSettingsAPI?.Details[userInfoState.id]?.view || {}).length;

  const stopProcessing = () => {
    setProcessing(false);
    history.replace(`${GlobalRouteNames.AccountSettingPage}${AccountSettingRouteNames.AutoReplenishmentPage}`);
  };

  useEffect(() => {
    if (!orderInfo) return;

    attempts.current += 1;

    if (orderInfo.length > 0) {
      const card = orderInfo[0]?.Card;
      if (card) {
        stopProcessing();
      } else if (attempts.current === 6) {
        stopProcessing();
        customEnqueueSnackbar(t('accountSettings.cardNotAdded'), 'error');
      }
    } else {
      stopProcessing();
    }
  }, [fulfilledTimeStamp]);

  useEffect(() => {
    if (!regularPaymentSettingsAPI) return;
    if (regularPaymentSettingsAPI?.Card) {
      setValue('paymentCard', `${regularPaymentSettingsAPI?.Card.id}` || '');
    }
    if (regularPaymentSettingsAPI && !isAutoReplenishmentSet.current) {
      setValue('isAutoReplenishment', !!isRegularPayment);
      isAutoReplenishmentSet.current = true;
    }

    setValue('autoReplenishmentEmail', `${regularPaymentSettingsAPI?.Email ?? ''}`);
  }, [regularPaymentSettingsAPI?.Active, regularPaymentSettingsAPI?.Email, regularPaymentSettingsAPI?.Card?.id]);

  useEffect(() => {
    isAutoReplenishmentSet.current = false;
  }, [listOfCardsAPI?.list.length]);

  const handleResetEmail = () => {
    if (!(isLoadingChangeEmail || isLoadingDeleteEmail)) {
      setValue('autoReplenishmentEmail', regularPaymentSettingsAPI?.Email ?? '');
    }
    clearErrors('autoReplenishmentEmail');
  };

  const handleAddNewCard = () => {
    handleResetEmail();
    dispatch(openModal({ modalVariants: ModalVariants.AddNewCard }));
  };

  const handleDeleteCard = async (cardId: string, cardNumber: string, isActiveCard: boolean) => {
    handleResetEmail();
    dispatch(openModal({ modalVariants: ModalVariants.DeleteCard, content: { cardId, cardNumber, isActiveCard } }));
  };

  const handleRegularPaymentSwitch = useCallback(
    useDebounce(async ({ cardId, status }: { cardId: string; status: 1 | 0 }) => {
      await switchRegularPayment({
        cardId,
        status,
      }).unwrap();
      if (status === 1) {
        customEnqueueSnackbar(t('snackbar.regularPaymentEnabled'), 'success');
      } else {
        customEnqueueSnackbar(t('snackbar.regularPaymentDisabled'), 'warning');
      }
    }, 500),
    [],
  );

  const handleSwitch = (isChecked: boolean) => {
    handleResetEmail();
    handleRegularPaymentSwitch({ cardId: methods.getValues('paymentCard'), status: isChecked ? 1 : 0 });
  };

  const handleDeleteRegularPaymentEmail = useDebounce(async () => {
    try {
      setValue('autoReplenishmentEmail', '');
      await deleteRegularPaymentEmail('').unwrap();
      customEnqueueSnackbar(t('snackbar.autoReplenishmentDeleteEmail'), 'warning');
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  }, 600);

  const handleSaveEmail = useDebounce(async ({ autoReplenishmentEmail }: AutoReplenishmentForm) => {
    try {
      await changeRegularPaymentEmail(autoReplenishmentEmail).unwrap();
      customEnqueueSnackbar(t('registration.yourDataHasBeenSaved'), 'success');
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  }, 600);

  const handleChangeCard = async (paymentCard: string) => {
    try {
      handleResetEmail();
      await setPaymentCard({ cardId: paymentCard }).unwrap();
      customEnqueueSnackbar(t('registration.yourDataHasBeenSaved'), 'success');
    } catch (err) {
      const message = getErrorMessage(err);
      customEnqueueSnackbar(message, 'error');
    }
  };

  const setErrorSwitch = () => {
    handleResetEmail();
    if (!regularPaymentSettingsAPI?.Card?.id) {
      setError('isAutoReplenishment', { message: t('accountSettings.errorEmptyCard') });
      return;
    }
    if (!Object.entries(regularPaymentSettingsAPI?.Details[userInfoState.id].view || {}).length) {
      setError('isAutoReplenishment', { message: t('accountSettings.unavailableRegularPayment') });
    }
  };

  const toggleServices = () => {
    setOpenServices((prev) => {
      if (prev) {
        localStorage.setItem(`${LocalStorageItems.ActiveServicesHidden}-${userInfoState.id}`, '1');
      } else {
        localStorage.removeItem(`${LocalStorageItems.ActiveServicesHidden}-${userInfoState.id}`);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (!userInfoState.id) return;

    const showActiveServices = localStorage.getItem(`${LocalStorageItems.ActiveServicesHidden}-${userInfoState.id}`);
    setOpenServices(!showActiveServices);
  }, [userInfoState.id]);

  useEffect(() => {
    refetch();
  }, [userInfoState.Language]);

  const getTableServices = (services: View): TableBodyCell[] => {
    return Object.keys(services)
      .map((key) => {
        return services[key].map((el: AccountView) => {
          return el.name
            ? {
                id: el.account_id,
                bodyCells: [
                  <div className="auto-replenishment__active__services-left">
                    <div>{el.name}</div>
                    {el.ex.map((ex) => (
                      <div key={ex}>{ex}</div>
                    ))}
                  </div>,
                  <div className="auto-replenishment__active__services-right">
                    <CustomNumberFormat
                      value={el.details?.service?.amo}
                      suffix={el.is_daily ? t('servicePayment.costPerDay') : t('servicePayment.costPerMonth')}
                    />
                    {el.details?.services_ex.map((exAmo: ExAmoView) => (
                      <div key={exAmo.id}>
                        <CustomNumberFormat value={exAmo.amo} suffix={t('servicePayment.costPerMonth')} />
                      </div>
                    ))}
                  </div>,
                ],
              }
            : null;
        });
      })
      .flat()
      .filter(Boolean);
  };

  if (processing) {
    return <AddingCard />;
  }

  const activeServices = Object.keys(regularPaymentSettingsAPI?.Details || {}).filter(
    (key) => getTableServices(regularPaymentSettingsAPI?.Details[key]?.view || {}).length > 0,
  );

  return (
    <div className="auto-replenishment">
      <FormProvider {...methods}>
        <div className="auto-replenishment__maine-container">
          <div className="auto-replenishment__switch-container">
            <article className="auto-replenishment__switch-container__title">
              {t('accountSettings.regularPayment')}
            </article>

            <FormInputSwitch
              handleChange={handleSwitch}
              isDisabled={
                (!isAvailableRegularPayment && !watch().isAutoReplenishment) || isFetchingCardList || isFetching
              }
              name="isAutoReplenishment"
              setErrors={setErrorSwitch}
            />
          </div>
          {errors.isAutoReplenishment?.message ? (
            <div className="auto-replenishment__attention-container">{errors.isAutoReplenishment?.message}</div>
          ) : null}
          <div className="auto-replenishment__description-container">
            <p>
              {t('accountSettings.regularPaymentRules')}{' '}
              <a href={process.env.REACT_APP_AUTO_REPLENISHMENT_RULES || ''} target="_blank" rel="noreferrer">
                {t('accountSettings.autoReplenishmentRulesLink')}
              </a>
            </p>
            <p className="auto-replenishment__description-container__next-date">
              <span>{t('accountSettings.theNearestPaymentDateIs')}</span>&nbsp;
              <span className={`${isLoading ? 'skeleton' : 'skeleton--disabled'}`}>
                <strong>
                  {formatDate(regularPaymentSettingsAPI?.NextPaymentDate || '', {
                    month: 'long',
                    day: 'numeric',
                  })}
                  {regularPaymentSettingsAPI?.NextPaymentDate ? `${t('accountSettings.till11AM')}` : ''}
                </strong>
              </span>
            </p>
          </div>
          <div className="auto-replenishment__bank-card-container">
            <article className="auto-replenishment__bank-card-container__title footnote_after">
              {t('accountSettings.selectedCard')}
            </article>
            {isLoading ? (
              <CustomSkeletonElement
                skeletonContainerClassName="skeleton-container"
                skeletonChildrenClassName="skeleton-children skeleton"
                childrenCount={2}
              />
            ) : (
              <PaymentCardsSelector
                handleChange={handleChangeCard}
                handleAddNewCard={handleAddNewCard}
                paymentCards={listOfCardsAPI}
                handleDeleteCard={handleDeleteCard}
                isRadioDisabled={false}
                regularPaymentCard={regularPaymentSettingsAPI?.Card}
              />
            )}
          </div>
          <div className="auto-replenishment__email-notification-container">
            <article className="auto-replenishment__email-notification-container__title">
              Email {t('accountSettings.receiptsEmail')}
            </article>
            <div
              className={`auto-replenishment__email-notification-container__input ${
                isLoading ? 'skeleton' : 'skeleton--disabled'
              }`}
            >
              <FormInputText
                name="autoReplenishmentEmail"
                className="primary"
                label={t('input.email')}
                minLength={4}
                maxLength={99}
                pattern={InputRegexPattern.Email}
              />
              <div className="auto-replenishment__email-notification-container__input-btn">
                <CustomMUIButton
                  label={<SaveEmailIcon />}
                  isDisabled={isSaveEmailDisabled}
                  handleClick={handleSubmit(handleSaveEmail)}
                  className={ButtonClassNames.IconFulfilled}
                  tooltipText={`${t('buttons.save')}`}
                />
                <CustomMUIButton
                  label={<DeleteIcon />}
                  isDisabled={isDeleteEmailDisabled}
                  handleClick={handleDeleteRegularPaymentEmail}
                  className={ButtonClassNames.IconFulfilled}
                  tooltipText={`${t('buttons.deleteEmail')}`}
                />
              </div>
            </div>
          </div>
          {activeServices.length > 0 && (
            <>
              <div className="auto-replenishment__active__services__header">
                <article className="auto-replenishment__active__services__header__title">
                  {t('services.active')}
                </article>
                <CustomMUIButton
                  label={openServices ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  handleClick={toggleServices}
                  className={ButtonClassNames.Icon}
                />
              </div>
              <Collapse in={openServices} timeout="auto" unmountOnExit>
                {activeServices.map((key) => (
                  <BasicTable
                    key={key}
                    className="auto-replenishment__active__services"
                    tableHead={[`${t('services.active')}`, `${t('servicePayment.pricePerMonth')}`]}
                    caption={
                      <>
                        <LocationIcon />{' '}
                        <span>
                          {regularPaymentSettingsAPI?.Details[key]?.alias ||
                            regularPaymentSettingsAPI?.Details[key]?.address}
                        </span>
                      </>
                    }
                    tableBodyCells={getTableServices(regularPaymentSettingsAPI?.Details[key]?.view || {})}
                  />
                ))}
              </Collapse>
            </>
          )}
          <div className="auto-replenishment__footer">
            <div className="auto-replenishment__footer__payment-amount">
              <div className="auto-replenishment__footer__payment-amount__label">
                {t('accountSettings.amountOfPayment')}
                <CustomMUIButton
                  label={<InfoIcon />}
                  className={ButtonClassNames.Icon}
                  tooltipText={`${t('accountSettings.chargedAmountMayDiff')}`}
                  isDisabled
                />
              </div>
              <p
                className={`auto-replenishment__footer__payment-amount__value ${
                  isLoading ? 'skeleton' : 'skeleton--disabled'
                }`}
              >{`${regularPaymentSettingsAPI?.Amount || 0} ${t('common.uah')}`}</p>
            </div>
          </div>
        </div>
      </FormProvider>
    </div>
  );
};
